import { shade, shadow, type Ctx } from './paint';
import type { CharLook, Pose } from './sprites';

/**
 * High-detail character renderer. Shapes are drawn with paths at half-pixel
 * precision (the canvas runs at ≥2 device px per logical px), with soft
 * shading and a "selective outline" (a darker tone of each part) instead of
 * hard black pixels.
 */
const lineOf = (c: string) => shade(c, -0.5);

function fillStroke(c: Ctx, fill: string | CanvasGradient, stroke: string, w = 0.6): void {
  c.fillStyle = fill;
  c.fill();
  c.strokeStyle = stroke;
  c.lineWidth = w;
  c.stroke();
}

function rr(c: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.roundRect(x, y, w, h, r);
}

function hgrad(c: Ctx, x: number, w: number, a: string, b: string): CanvasGradient {
  const g = c.createLinearGradient(x, 0, x + w, 0);
  g.addColorStop(0, a);
  g.addColorStop(1, b);
  return g;
}

export function drawCharacterHD(c: Ctx, look: CharLook, x: number, y: number, pose: Pose): void {
  if (pose.down) return lying(c, look, x, y);
  const f = pose.facing;
  const side = f === 'left' || f === 'right';
  const dir = f === 'left' ? -1 : 1;
  const step = pose.moving ? Math.sin(pose.phase * Math.PI * 2) : 0;
  const stepAbs = Math.abs(step);
  const bob = pose.moving ? -stepAbs * 1.1 : (pose.breath > 0.5 ? -0.4 : 0);
  const small = !!look.small;
  const legH = small ? 4 : 5.5;
  const torsoH = small ? 7 : 8.5;
  const headW = small ? 11 : 12;
  const headH = small ? 10.5 : 11;

  const z = pose.z ?? 0;
  const k = 1 - Math.min(0.45, z / 30);
  shadow(c, x, y - 0.5, (small ? 5.5 : 7) * k, 2.2 * k, 0.3, small ? 18 : 26);
  y -= z;

  const hip = y - legH - 1 + bob * 0.3;
  const shoulder = hip - torsoH + 1 + bob * 0.7;
  const headBottom = shoulder + 1.2 + bob * 0.2;
  const headTop = headBottom - headH;

  c.save();
  c.lineJoin = 'round';

  // --- Hair that hangs behind the head (drawn first) ---------------------------
  hairBack(c, look, x, headTop, headW, headH, f, pose);

  // --- Legs and shoes ----------------------------------------------------------
  const legW = small ? 2.2 : 2.6;
  const legs = side
    ? [
        { lx: x - legW / 2 + step * 2.2 * dir, lift: Math.max(0, -step) * 1.2, back: step < 0 },
        { lx: x - legW / 2 - step * 2.2 * dir, lift: Math.max(0, step) * 1.2, back: step >= 0 },
      ]
    : [
        { lx: x - legW - 0.6, lift: Math.max(0, step) * 1.4, back: false },
        { lx: x + 0.6, lift: Math.max(0, -step) * 1.4, back: false },
      ];
  for (const L of legs.sort((a, b) => Number(b.back) - Number(a.back))) {
    const top = hip - 0.5, bottom = y - 1.6 - L.lift;
    rr(c, L.lx, top, legW, bottom - top + 0.5, 0.8);
    fillStroke(c, L.back ? shade(look.bottom, -0.15) : look.bottom, lineOf(look.bottom), 0.5);
    if (!look.dress || small) {
      // Socks.
      c.fillStyle = '#f6f3ee';
      c.fillRect(L.lx + 0.2, bottom - 1.2, legW - 0.4, 0.9);
    }
    const sx = side ? L.lx + (dir > 0 ? -0.3 : -1.2) : L.lx - 0.4;
    rr(c, sx, bottom - 0.6, legW + 1.5, 2.2, 1);
    fillStroke(c, look.shoes, lineOf(look.shoes), 0.5);
    c.fillStyle = 'rgba(255,255,255,0.45)';
    c.fillRect(sx + 0.6, bottom - 0.2, 1, 0.5);
  }

  // --- Far arm (behind the body when seen from the side) ------------------------
  const armLen = small ? 5 : 6.5;
  const swing = step * 0.55;
  if (side) arm(c, look, x - dir * 0.5, shoulder + 1, armLen, -swing * dir, true);

  // --- Torso -----------------------------------------------------------------------
  const tw = small ? 9.5 : 11;
  const tx = x - tw / 2;
  c.beginPath();
  c.roundRect(tx, shoulder, tw, torsoH, [2.5, 2.5, 1.2, 1.2]);
  fillStroke(c, hgrad(c, tx, tw, shade(look.top, 0.12), look.topShade), lineOf(look.top));
  // Lower half: skirt flares, trousers are straight.
  if (look.dress) {
    c.beginPath();
    c.moveTo(tx + 0.5, hip - 3);
    c.lineTo(tx + tw - 0.5, hip - 3);
    c.lineTo(tx + tw + 1.2, hip + 1.2);
    c.quadraticCurveTo(x, hip + 2, tx - 1.2, hip + 1.2);
    c.closePath();
    fillStroke(c, hgrad(c, tx - 1, tw + 2, shade(look.bottom, 0.1), shade(look.bottom, -0.15)), lineOf(look.bottom));
    c.strokeStyle = shade(look.bottom, -0.25);
    c.lineWidth = 0.4;
    c.beginPath();
    for (const k of [-0.3, 0, 0.3]) {
      c.moveTo(x + k * tw, hip - 2.5);
      c.lineTo(x + k * tw * 1.25, hip + 1.2);
    }
    c.stroke();
  } else {
    rr(c, tx, hip - 2.5, tw, 3, 0.8);
    fillStroke(c, look.bottom, lineOf(look.bottom), 0.5);
  }
  // Uniform / clothing details.
  if (f !== 'up') {
    if (look.apron) {
      rr(c, x - 3.5 + (side ? dir * 1.5 : 0), shoulder + 2.5, 7, torsoH + (look.dress ? 2 : -0.5), 1);
      fillStroke(c, look.apron, shade(look.apron, -0.25), 0.4);
    }
    if (look.top === '#f4f1ea' && !side) {
      // School uniform: sailor-style collar with a little ribbon.
      c.fillStyle = '#2f4a8a';
      c.beginPath();
      c.moveTo(x - 3.5, shoulder + 0.2);
      c.lineTo(x, shoulder + 3.8);
      c.lineTo(x + 3.5, shoulder + 0.2);
      c.lineTo(x + 2, shoulder + 0.2);
      c.lineTo(x, shoulder + 2.2);
      c.lineTo(x - 2, shoulder + 0.2);
      c.fill();
      c.fillStyle = look.badge ?? '#e2493b';
      c.beginPath();
      c.moveTo(x, shoulder + 3.6);
      c.lineTo(x - 1.6, shoulder + 5.2);
      c.lineTo(x + 1.6, shoulder + 5.2);
      c.fill();
    } else if (!side && !look.apron) {
      c.fillStyle = shade(look.top, -0.3);
      c.beginPath();
      c.moveTo(x - 2, shoulder + 0.2);
      c.lineTo(x, shoulder + 2);
      c.lineTo(x + 2, shoulder + 0.2);
      c.fill();
      if (look.badge) {
        c.fillStyle = look.badge;
        c.fillRect(x + 2, shoulder + 2.5, 1.5, 1.5);
      }
    }
    if (look.tie && !side) {
      c.fillStyle = look.tie;
      c.beginPath();
      c.moveTo(x - 0.7, shoulder + 0.5);
      c.lineTo(x + 0.7, shoulder + 0.5);
      c.lineTo(x + 1, shoulder + 5.5);
      c.lineTo(x, shoulder + 6.5);
      c.lineTo(x - 1, shoulder + 5.5);
      c.fill();
    }
  }

  // --- Near arms -----------------------------------------------------------------------
  if (side) arm(c, look, x + dir * 0.5, shoulder + 1, armLen, swing * dir, false);
  else {
    arm(c, look, x - tw / 2 - 0.2, shoulder + 1, armLen, swing * 0.6, false);
    arm(c, look, x + tw / 2 + 0.2, shoulder + 1, armLen, -swing * 0.6, false);
  }

  // --- Head ----------------------------------------------------------------------------------
  const hx = x - headW / 2 + (side ? dir * 0.6 : 0);
  // Neck.
  c.fillStyle = shade(look.skin, -0.18);
  c.fillRect(x - 1.2, headBottom - 1, 2.4, 1.6);
  rr(c, hx, headTop, headW, headH, small ? 4.2 : 4.5);
  const skinG = c.createLinearGradient(hx, headTop, hx + headW, headTop + headH);
  skinG.addColorStop(0, shade(look.skin, 0.1));
  skinG.addColorStop(1, shade(look.skin, -0.1));
  fillStroke(c, skinG, lineOf(look.skin));
  if (side) {
    // Ear.
    c.beginPath();
    c.ellipse(x - dir * 1.2, headTop + headH * 0.55, 1.1, 1.5, 0, 0, Math.PI * 2);
    fillStroke(c, shade(look.skin, -0.06), lineOf(look.skin), 0.4);
  }
  face(c, look, x + (side ? dir * 0.6 : 0), headTop, headW, headH, f, pose.blink);
  hairFront(c, look, hx, headTop, headW, headH, f, pose);
  c.restore();
}

function arm(c: Ctx, look: CharLook, sx: number, sy: number, len: number, angle: number, far: boolean): void {
  c.save();
  c.translate(sx, sy);
  c.rotate(angle);
  const sleeve = far ? shade(look.top, -0.18) : look.top;
  rr(c, -1.1, -0.5, 2.2, len * 0.65, 1);
  fillStroke(c, sleeve, lineOf(look.top), 0.5);
  rr(c, -0.95, len * 0.55, 1.9, len * 0.35, 0.9);
  fillStroke(c, far ? shade(look.skin, -0.12) : look.skin, lineOf(look.skin), 0.4);
  c.restore();
}

function face(c: Ctx, look: CharLook, cx: number, top: number, _w: number, h: number, f: Pose['facing'], blink: boolean): void {
  if (f === 'up') return;
  const eyeY = top + h * 0.56;
  const side = f !== 'down';
  const dir = f === 'left' ? -1 : 1;
  const eyes = side ? [cx + dir * 2.4] : [cx - 2.4, cx + 2.4];
  for (const ex of eyes) {
    if (blink) {
      c.strokeStyle = '#2b2233';
      c.lineWidth = 0.6;
      c.beginPath();
      c.moveTo(ex - 1, eyeY + 0.6);
      c.quadraticCurveTo(ex, eyeY + 1.3, ex + 1, eyeY + 0.6);
      c.stroke();
    } else {
      c.fillStyle = '#2b2233';
      c.beginPath();
      c.ellipse(ex, eyeY, side ? 0.75 : 0.95, 1.35, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#6b4a3a';
      c.beginPath();
      c.ellipse(ex, eyeY + 0.45, side ? 0.5 : 0.65, 0.6, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.arc(ex - 0.35, eyeY - 0.5, 0.42, 0, Math.PI * 2);
      c.fill();
    }
    // Eyebrow.
    c.strokeStyle = shade(look.hair, -0.1);
    c.lineWidth = 0.5;
    c.beginPath();
    c.moveTo(ex - 0.9, eyeY - 2.1);
    c.lineTo(ex + 0.9, eyeY - 2.3);
    c.stroke();
  }
  // Blush and mouth.
  c.fillStyle = 'rgba(255,120,130,0.35)';
  for (const ex of side ? [cx + dir * 1.6] : [cx - 3.6, cx + 3.6]) {
    c.beginPath();
    c.ellipse(ex, eyeY + 2, 1.1, 0.6, 0, 0, Math.PI * 2);
    c.fill();
  }
  c.strokeStyle = '#a24a44';
  c.lineWidth = 0.5;
  c.beginPath();
  const mx = side ? cx + dir * 3.2 : cx;
  c.moveTo(mx - 0.8, eyeY + 2.6);
  c.quadraticCurveTo(mx, eyeY + 3.3, mx + 0.8, eyeY + 2.6);
  c.stroke();
  if (side) {
    c.fillStyle = shade(look.skin, -0.12);
    c.fillRect(cx + dir * 5.4 - (dir < 0 ? 0.5 : 0), eyeY + 0.6, 0.5, 1);
  }
  if (look.glasses) {
    c.strokeStyle = '#3a2a22';
    c.lineWidth = 0.45;
    for (const ex of eyes) {
      c.beginPath();
      c.roundRect(ex - 1.7, eyeY - 1.4, 3.4, 2.8, 0.8);
      c.stroke();
    }
    if (!side) {
      c.beginPath();
      c.moveTo(eyes[0] + 1.7, eyeY - 0.4);
      c.lineTo(eyes[1] - 1.7, eyeY - 0.4);
      c.stroke();
    }
  }
}

function hairBack(c: Ctx, look: CharLook, x: number, top: number, w: number, h: number, f: Pose['facing'], pose: Pose): void {
  const hair = look.hair;
  const sway = pose.moving ? Math.sin(pose.phase * Math.PI * 2 + 1) * 0.8 : Math.sin(pose.breath * Math.PI * 2) * 0.2;
  c.fillStyle = shade(hair, -0.08);
  c.strokeStyle = lineOf(hair);
  c.lineWidth = 0.6;
  if (look.hairStyle === 'long') {
    c.beginPath();
    c.moveTo(x - w / 2 - 0.6, top + 4);
    c.quadraticCurveTo(x - w / 2 - 1.5 + sway, top + h + 4, x - w / 2 + 1 + sway, top + h + 6);
    c.lineTo(x + w / 2 - 1 + sway, top + h + 6);
    c.quadraticCurveTo(x + w / 2 + 1.5 + sway, top + h + 4, x + w / 2 + 0.6, top + 4);
    c.closePath();
    c.fill();
    c.stroke();
  } else if (look.hairStyle === 'pony') {
    const d = f === 'left' ? 1 : f === 'right' ? -1 : 0;
    const px = f === 'up' ? x : x + d * (w / 2) + (f === 'down' ? w / 2 - 1 : 0);
    c.beginPath();
    c.ellipse(px + sway + d * 1.5, top + h * 0.75, 2.1, 4.2, sway * 0.15 + d * 0.3, 0, Math.PI * 2);
    c.fill();
    c.stroke();
  } else if (look.hairStyle === 'pigtails') {
    for (const s of [-1, 1]) {
      c.beginPath();
      c.ellipse(x + s * (w / 2 + 1.2), top + h * 0.6 + Math.abs(sway) * 0.5, 1.8, 3, s * 0.4 + sway * 0.1, 0, Math.PI * 2);
      c.fill();
      c.stroke();
    }
  } else if (look.hairStyle === 'bun') {
    c.beginPath();
    c.arc(x + (f === 'left' ? 2.5 : f === 'right' ? -2.5 : 0), top - 0.5, 2.6, 0, Math.PI * 2);
    c.fill();
    c.stroke();
  }
}

function hairFront(c: Ctx, look: CharLook, hx: number, top: number, w: number, h: number, f: Pose['facing'], pose: Pose): void {
  const hair = look.hair;
  const cx = hx + w / 2;
  const dir = f === 'left' ? -1 : f === 'right' ? 1 : 0;
  const g = c.createLinearGradient(hx, top - 2, hx + w, top + h * 0.6);
  g.addColorStop(0, shade(hair, 0.18));
  g.addColorStop(1, shade(hair, -0.12));
  c.strokeStyle = lineOf(hair);
  c.lineWidth = 0.6;

  if (look.hairStyle === 'cap') {
    const hat = look.hat ?? hair;
    // Hair peeking out, cap dome and brim.
    c.fillStyle = hair;
    c.fillRect(hx + 0.6, top + 3.5, 1.6, 3);
    c.fillRect(hx + w - 2.2, top + 3.5, 1.6, 3);
    c.beginPath();
    c.moveTo(hx - 0.4, top + 4.2);
    c.quadraticCurveTo(hx - 0.2, top - 2, cx, top - 2.2);
    c.quadraticCurveTo(hx + w + 0.2, top - 2, hx + w + 0.4, top + 4.2);
    c.closePath();
    fillStroke(c, hgrad(c, hx, w, shade(hat, 0.2), shade(hat, -0.15)), lineOf(hat));
    if (f !== 'up') {
      c.beginPath();
      if (dir) c.ellipse(cx + dir * 4.5, top + 4, 4, 1.2, 0, 0, Math.PI * 2);
      else c.ellipse(cx, top + 4.4, w / 2 + 0.6, 1.4, 0, 0, Math.PI);
      fillStroke(c, shade(hat, -0.15), lineOf(hat), 0.5);
    }
    c.fillStyle = 'rgba(255,255,255,0.3)';
    c.fillRect(cx - 2.5, top - 0.8, 3, 0.6);
    if (look.badge && f === 'down') {
      c.fillStyle = look.badge;
      c.fillRect(cx - 0.7, top + 0.6, 1.4, 1.4);
    }
    return;
  }

  c.beginPath();
  if (f === 'up') {
    // Back of the head: hair covers it entirely.
    c.roundRect(hx - 0.6, top - 1.2, w + 1.2, h * 0.92, [5, 5, 3, 3]);
    fillStroke(c, g, lineOf(hair));
    c.strokeStyle = shade(hair, -0.25);
    c.lineWidth = 0.4;
    c.beginPath();
    c.moveTo(cx, top);
    c.lineTo(cx + 0.5, top + h * 0.6);
    c.stroke();
  } else {
    // Cap of hair over the top of the head, then a fringe of pointed locks.
    const fringeY = top + h * (look.hairStyle === 'neat' ? 0.3 : 0.38);
    c.moveTo(hx - 0.7, top + h * 0.62);
    c.quadraticCurveTo(hx - 1, top - 1.6, cx, top - 1.8);
    c.quadraticCurveTo(hx + w + 1, top - 1.6, hx + w + 0.7, top + h * 0.62);
    // Fringe locks (zig-zag), shifted towards the facing side.
    const n = look.hairStyle === 'messy' ? 6 : 5;
    const span = w + 1.4;
    for (let i = n; i >= 0; i--) {
      const lx = hx - 0.7 + (span * i) / n + dir * 0.8;
      const ly = i % 2 ? fringeY + 1.6 : fringeY - 0.4;
      c.lineTo(lx, ly);
    }
    c.closePath();
    fillStroke(c, g, lineOf(hair));
    if (look.hairStyle === 'curly') {
      for (let i = 0; i < 7; i++) {
        c.beginPath();
        c.arc(hx + (w / 6) * i, top - 0.6 + (i % 2) * 0.8, 1.6, 0, Math.PI * 2);
        fillStroke(c, shade(hair, (i % 2) * 0.08), lineOf(hair), 0.4);
      }
    }
    if (look.hairStyle === 'messy') {
      c.beginPath();
      c.moveTo(cx - 2, top - 1.4);
      c.lineTo(cx - 1, top - 3.6);
      c.lineTo(cx + 0.3, top - 1.5);
      c.lineTo(cx + 1.8, top - 3);
      c.lineTo(cx + 2.4, top - 1.2);
      fillStroke(c, hair, lineOf(hair), 0.5);
    }
    if (look.hairStyle === 'long' || look.hairStyle === 'pigtails' || look.hairStyle === 'bun') {
      // Side locks framing the face.
      for (const s of dir ? [-dir] : [-1, 1]) {
        c.beginPath();
        const sx = s < 0 ? hx - 0.6 : hx + w + 0.6;
        c.moveTo(sx, top + 2);
        c.quadraticCurveTo(sx + s * 0.8, top + h * 0.7, sx - s * 1.2, top + h * (look.hairStyle === 'long' ? 1.05 : 0.8));
        c.lineTo(sx - s * 2.4, top + h * 0.45);
        c.closePath();
        fillStroke(c, shade(hair, -0.05), lineOf(hair), 0.5);
      }
    }
  }
  // Glossy highlight arc.
  c.strokeStyle = 'rgba(255,255,255,0.38)';
  c.lineWidth = 0.7;
  c.beginPath();
  c.arc(cx - 1 - dir, top + 2.5, w * 0.36, Math.PI * 1.1, Math.PI * 1.45);
  c.stroke();
  void pose;
}

function lying(c: Ctx, look: CharLook, x: number, y: number): void {
  shadow(c, x, y - 1.5, 14, 3);
  c.save();
  c.translate(x, y - 5);
  c.rotate(-Math.PI / 2);
  // Draw a standing pose rotated onto the floor.
  c.translate(0, 13);
  drawCharacterHD(c, look, 0, 0, { facing: 'down', phase: 0, moving: false, blink: true, breath: 0 });
  c.restore();
}
